import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AnimatedOutlet } from '../components/AnimatedOutlet';
import { ROUTES } from './routeConfig';

/**
 * PublicOnlyRoute
 *
 * Wraps auth-related routes (sign in, sign up, etc.) that should only
 * be accessible to unauthenticated users. Redirects authenticated
 * users to the dashboard (or to the originally intended destination
 * if a post-login redirect was saved in location state).
 * Uses AnimatedOutlet for smooth route transition animations
 * between auth pages.
 */
export const PublicOnlyRoute: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show nothing while auth state is loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-dark-900">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-dark-500 dark:text-dark-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Check if there's a saved location to redirect back to after login
    const from = (location.state as { from?: { pathname: string } })?.from;
    const redirectTo = from?.pathname || ROUTES.DASHBOARD;

    return <Navigate to={redirectTo} replace />;
  }

  return <AnimatedOutlet />;
};

PublicOnlyRoute.displayName = 'PublicOnlyRoute';